import React from "react";
import { Progress, Tooltip, Badge } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useConsultantGuaranteedHours } from "../../networking/guaranteedHours.hooks";
import AppText, { FormLabel } from "../Core/AppText/AppText";
import { chartColors } from "../../theme/chartColors";

export interface GuaranteedHoursProgressProps {
	consultantId: string;
	month?: string;
	monthSelector?: React.ReactNode;
}

const GuaranteedHoursProgress: React.FC<GuaranteedHoursProgressProps> = ({
	consultantId,
	month,
	monthSelector,
}) => {
	const { guaranteedHoursProgress, isLoading, isError } = useConsultantGuaranteedHours(
		consultantId,
		month
	);

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4">
				<FormLabel showKnowledgeBaseForTagOnHover="consultant-guaranteed-hours">
					Garantitid
				</FormLabel>
				<div className="p-6 bg-white rounded border border-gray-300">
					<AppText size="s" color="grayText">
						Laddar...
					</AppText>
				</div>
			</div>
		);
	}

	if (isError || !guaranteedHoursProgress) {
		return (
			<div className="flex flex-col gap-2">
				<FormLabel showKnowledgeBaseForTagOnHover="consultant-guaranteed-hours">
					Garantitid
				</FormLabel>
				<div className="p-6 bg-white rounded border border-gray-300">
					<AppText size="s" color="grayText">
						Ingen data tillgänglig
					</AppText>
				</div>
			</div>
		);
	}

	if (guaranteedHoursProgress.guaranteedHours === 0) {
		return (
			<div className="flex flex-col gap-2">
				<div className="flex">
					<FormLabel margin="0" showKnowledgeBaseForTagOnHover="consultant-guaranteed-hours">
						Garantitid
					</FormLabel>
				</div>
				<div className="p-6 bg-white rounded border border-gray-300">
					<AppText size="s" color="grayText">
						Omfattas ej av garantitid
					</AppText>
				</div>
			</div>
		);
	}

	const {
		actualHours,
		guaranteedHours,
		remainingHours,
		isProRated,
		proRatingInfo,
		consultantGuaranteedHoursPerMonth,
		collectiveAgreementGuaranteedHoursPerMonth,
	} = guaranteedHoursProgress;
	const percentage = guaranteedHours > 0 ? (actualHours / guaranteedHours) * 100 : 0;
	const isComplete = actualHours >= guaranteedHours;

	// Check if consultant's frozen value differs from collective agreement
	const hasDifferentValue =
		consultantGuaranteedHoursPerMonth !== undefined &&
		collectiveAgreementGuaranteedHoursPerMonth !== undefined &&
		consultantGuaranteedHoursPerMonth !== collectiveAgreementGuaranteedHoursPerMonth;

	return (
		<div className="flex flex-col gap-2">
			<div className="flex justify-between items-center">
				<FormLabel margin="0" showKnowledgeBaseForTagOnHover="consultant-guaranteed-hours">
					Garantitid
				</FormLabel>
			</div>
			<div className="flex flex-col gap-4 p-6 bg-white rounded border border-gray-300">
				{isProRated && proRatingInfo && (
					<div className="flex items-center gap-2">
						<Tooltip
							label={
								proRatingInfo.formula ||
								`Justerad: ${proRatingInfo.remainingDays} dagar / ${proRatingInfo.daysInMonth} dagar`
							}
							placement="top"
							hasArrow
						>
							<Badge colorScheme="blue" fontSize="xs" cursor="pointer">
								Justerad
							</Badge>
						</Tooltip>
						<AppText size="xs" color="grayText">
							från {dayjs(proRatingInfo.startDate).format("D MMM YYYY")}
						</AppText>
					</div>
				)}

				{hasDifferentValue && (
					<div className="flex items-center gap-2">
						<Badge colorScheme="orange" fontSize="xs">
							Frozen: {consultantGuaranteedHoursPerMonth} h
						</Badge>
						<AppText size="xs" color="grayText">
							(Agreement: {collectiveAgreementGuaranteedHoursPerMonth} h)
						</AppText>
					</div>
				)}

				<div className="grid grid-cols-3 gap-4">
					<div className="flex flex-col gap-1">
						<AppText size="xs" color="grayText">
							Garanterade timmar
						</AppText>
						<AppText size="l" weight="bold">
							{Math.round(guaranteedHours)} h
						</AppText>
					</div>
					<div className="flex flex-col gap-1">
						<AppText size="xs" color="grayText">
							Arbetade timmar
						</AppText>
						<AppText size="l" weight="bold" color={isComplete ? "green" : "primary"}>
							{Math.round(actualHours)} h
						</AppText>
					</div>
					<div className="flex flex-col gap-1">
						<AppText size="xs" color="grayText">
							Återstående timmar
						</AppText>
						<AppText size="l" weight="bold" color={remainingHours > 0 ? "orange" : "green"}>
							{Math.max(0, Math.round(remainingHours))} h
						</AppText>
					</div>
				</div>

				<div className="flex flex-col gap-2">
					<div className="flex justify-between items-center">
						<AppText size="xs" weight="medium" color={isComplete ? "green" : "grayText"}>
							{Math.round(percentage)}%
						</AppText>
					</div>
					<Progress
						value={Math.min(percentage, 100)}
						size="sm"
						colorScheme={isComplete ? "green" : "orange"}
						borderRadius="full"
					/>
				</div>

				{isComplete && (
					<div
						className="flex items-center gap-2 p-3 rounded"
						style={{ backgroundColor: "#E6F7ED", border: "1px solid hsl(var(--success))" }}
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M13.3333 4L6 11.3333L2.66667 8"
								stroke={chartColors.success}
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<AppText size="s" weight="medium" color="green">
							Garantitid uppfylld för denna månad
						</AppText>
					</div>
				)}
			</div>
		</div>
	);
};
export default GuaranteedHoursProgress;
